import { useState, useEffect, useCallback } from 'react';

import {
  InsiderCourseComplete,
  InsiderListingPageView,
  InsiderProductPageView,
  InsiderPurchasePageView,
  InsiderUserData,
  UseInsiderOptions,
} from '@common/type/InsiderType';
import config from 'src/config';

declare global {
  interface Window {
    InsiderQueue: unknown[];
  }
}

export const useInsider = (options: UseInsiderOptions = {}) => {
  const { onScriptLoad, onScriptError, autoLoad } = options;

  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isScriptLoading, setIsScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState<Error | null>(null);

  // Load the Insider script dynamically
  const loadInsiderScript = useCallback(() => {
    if (isScriptLoaded || isScriptLoading) {
      return Promise.resolve();
    }

    setIsScriptLoading(true);
    setScriptError(null);

    return new Promise<void>((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(`script[src*="${config.INSIDER_SCRIPT_URL}"]`);
      if (existingScript) {
        setIsScriptLoaded(true);
        setIsScriptLoading(false);
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `${config.INSIDER_SCRIPT_URL}?id=${config.INSIDER_PARTNER_ID}`;
      script.async = true;

      script.onload = () => {
        setIsScriptLoaded(true);
        setIsScriptLoading(false);
        onScriptLoad?.();
        resolve();
      };

      script.onerror = (_) => {
        const error = new Error(`Failed to load Insider script: ${config.INSIDER_SCRIPT_URL}`);
        setScriptError(error);
        setIsScriptLoading(false);
        onScriptError?.(error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }, [isScriptLoaded, isScriptLoading, onScriptLoad, onScriptError]);

  const insider_dispatch = async (eventList: (() => Promise<void>)[]) => {
    window.InsiderQueue = window.InsiderQueue || [];

    await Promise.all(eventList.map((event) => event()));

    window.InsiderQueue.push({
      type: 'init',
    });

    await loadInsiderScript();
  };

  const sendUserInformation = useCallback(async (userData: InsiderUserData) => {
    const { uid, email, birthday, gdpr_optin, name, surname, username, phone_number, age } = userData;

    window.InsiderQueue.push({
      type: 'user',
      value: {
        uuid: uid,
        email,
        birthday,
        gdpr_optin,
        name,
        surname,
        username,
        age,
        email_optin: true,
        phone_number,
      },
    });

    window.InsiderQueue.push({
      type: 'init',
    });
  }, []);

  const sendHomeEvent = useCallback(async () => {
    window.InsiderQueue.push({
      type: 'home',
    });

    window.InsiderQueue.push({
      type: 'init',
    });
  }, []);

  const sendListingPageView = useCallback(async (params: InsiderListingPageView) => {
    window.InsiderQueue.push({
      type: 'category',
      value: params,
    });

    window.InsiderQueue.push({
      type: 'init',
    });
  }, []);

  const sendProductPageView = useCallback(async (params: InsiderProductPageView) => {
    window.InsiderQueue.push({
      type: 'product',
      value: params,
    });

    window.InsiderQueue.push({
      type: 'currency',
      value: 'THB',
    });

    window.InsiderQueue.push({
      type: 'init',
    });
  }, []);

  const sendPurchasePageView = useCallback(async (params: InsiderPurchasePageView) => {
    window.InsiderQueue.push({
      type: 'purchase',
      value: params,
    });

    window.InsiderQueue.push({
      type: 'currency',
      value: 'THB',
    });

    window.InsiderQueue.push({
      type: 'init',
    });
  }, []);

  const sendCourseComplete60 = useCallback(async (params: InsiderCourseComplete) => {
    window.InsiderQueue.push({
      type: 'custom_event',
      value: [
        {
          event_name: 'course_complete_60',
          event_parameters: params,
        },
      ],
    });
  }, []);

  const sendCourseComplete80 = useCallback(async (params: InsiderCourseComplete) => {
    window.InsiderQueue.push({
      type: 'custom_event',
      value: [
        {
          event_name: 'course_complete_80',
          event_parameters: params,
        },
      ],
    });
  }, []);

  const sendCourseCompleted = useCallback(async (params: InsiderCourseComplete) => {
    window.InsiderQueue.push({
      type: 'custom_event',
      value: [
        {
          event_name: 'course_complete',
          event_parameters: params,
        },
      ],
    });
  }, []);

  // Auto-load script if configured
  useEffect(() => {
    if (autoLoad && !isScriptLoaded && !isScriptLoading) {
      loadInsiderScript();
    }
  }, [autoLoad, isScriptLoaded, isScriptLoading, loadInsiderScript]);

  return {
    // Methods
    insider_dispatch,
    loadInsiderScript,
    sendUserInformation,
    sendHomeEvent,
    sendListingPageView,
    sendProductPageView,
    sendPurchasePageView,
    sendCourseComplete60,
    sendCourseComplete80,
    sendCourseCompleted,

    // State
    isScriptLoaded,
    isScriptLoading,
    scriptError,

    // Configuration
    config,
  };
};
